/**
 * Minimal chat widget entry. Wires DOM events to the typed API client and
 * renders responses. Logic that's interesting to test (status mapping,
 * locale detection, price formatting) lives in sibling modules; this file
 * is the side-effecting glue.
 */

import { postChat, ApiError } from './api';
import { errorMessage, resolveLocale, uiCopy, type Locale } from './i18n';
import { formatPrice } from './format';
import { safeHttpUrl } from './url';
import type { ChatRequest, ChatResponse, ProductCard } from './types';

const API_URL =
  (import.meta.env.VITE_CHAT_API_URL as string | undefined) ??
  (import.meta.env.DEV ? 'http://localhost:8787/chat' : '/chat');

const locale: Locale = resolveLocale(window.location.search, document.documentElement.lang);
// Sync <html lang> to the resolved locale so screen readers and any code
// reading the attribute later see the right value.
document.documentElement.lang = locale;
const ui = uiCopy(locale);
const sessionId = getOrCreateSessionId();

const chatEl = document.getElementById('chat')!;
const formEl = document.getElementById('composer') as HTMLFormElement;
const inputEl = document.getElementById('input') as HTMLInputElement;
const sendBtn = document.getElementById('send') as HTMLButtonElement;

// Swap static chrome from index.html (which always ships English) to the
// resolved locale. Doing it imperatively keeps a single deployed bundle
// serving both languages via `?lang=`.
const kickerEl = document.querySelector<HTMLParagraphElement>('.kicker');
if (kickerEl) kickerEl.textContent = ui.kicker;
inputEl.placeholder = ui.inputPlaceholder;
inputEl.setAttribute('aria-label', ui.inputAriaLabel);
sendBtn.textContent = ui.sendButton;

formEl.addEventListener('submit', async (e) => {
  e.preventDefault();
  const message = inputEl.value.trim();
  if (!message) return;

  appendUserMessage(message);
  inputEl.value = '';
  setBusy(true);

  const thinkingNode = appendThinking();

  const request: ChatRequest = {
    session_id: sessionId,
    message,
    store_view: locale,
  };

  try {
    const data = await postChat(API_URL, request);
    thinkingNode.remove();
    appendAssistantMessage(data);
  } catch (err) {
    thinkingNode.remove();
    if (err instanceof ApiError) {
      appendError(errorMessage(err.kind, locale));
    } else {
      appendError(errorMessage('generic', locale));
    }
  } finally {
    setBusy(false);
    inputEl.focus();
  }
});

function getOrCreateSessionId(): string {
  const KEY = '50ml-consultant-session';
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

function setBusy(busy: boolean): void {
  sendBtn.disabled = busy;
  inputEl.disabled = busy;
}

function appendUserMessage(text: string): void {
  const div = document.createElement('div');
  div.className = 'msg msg-user';
  div.textContent = text;
  chatEl.appendChild(div);
  chatEl.scrollTop = chatEl.scrollHeight;
}

function appendThinking(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'msg msg-thinking';
  div.setAttribute('aria-label', ui.thinkingAriaLabel);

  // Three dots — CSS animates them to indicate the request is in flight,
  // since a static "…" is too easy to mistake for a finished message.
  for (let i = 0; i < 3; i++) {
    const dot = document.createElement('span');
    dot.className = 'dot';
    div.appendChild(dot);
  }

  chatEl.appendChild(div);
  chatEl.scrollTop = chatEl.scrollHeight;
  return div;
}

function appendAssistantMessage(data: ChatResponse): void {
  const wrap = document.createElement('div');
  wrap.className = 'msg msg-assistant';

  const text = document.createElement('p');
  text.textContent = data.text;
  wrap.appendChild(text);

  if (data.cards.length > 0) {
    const list = document.createElement('div');
    list.className = 'cards';
    for (const card of data.cards) {
      list.appendChild(renderCard(card));
    }
    wrap.appendChild(list);
  }

  chatEl.appendChild(wrap);
  chatEl.scrollTop = chatEl.scrollHeight;
}

function renderCard(card: ProductCard): HTMLElement {
  const a = document.createElement('a');
  a.className = 'card';

  // Only set href if the URL is http(s). Non-http(s) schemes (javascript:,
  // data:, vbscript:, ...) get silently dropped — the card stays visible
  // with all the catalogue metadata, just not clickable. This is the
  // trust gate against stored XSS from a misconfigured / compromised
  // Magento publisher writing adversarial URLs into D1.
  const safeHref = safeHttpUrl(card.url);
  if (safeHref) {
    a.href = safeHref;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
  }

  const safeImgSrc = safeHttpUrl(card.image_url);
  if (safeImgSrc) {
    const img = document.createElement('img');
    img.src = safeImgSrc;
    img.alt = card.name;
    img.loading = 'lazy';
    // Hide a broken image rather than leaving the broken-image icon — the
    // card is still usable as a clickable link to the product page.
    img.addEventListener('error', () => img.remove(), { once: true });
    a.appendChild(img);
  }

  const meta = document.createElement('div');
  meta.className = 'card-meta';

  if (card.brand) {
    const brand = document.createElement('div');
    brand.className = 'card-brand';
    brand.textContent = card.brand;
    meta.appendChild(brand);
  }

  const name = document.createElement('div');
  name.className = 'card-name';
  name.textContent = card.name;
  meta.appendChild(name);

  const price = document.createElement('div');
  price.className = 'card-price';
  price.textContent = formatPrice(card.price, card.currency);
  meta.appendChild(price);

  a.appendChild(meta);
  return a;
}

function appendError(text: string): void {
  const div = document.createElement('div');
  div.className = 'msg msg-error';
  div.setAttribute('role', 'alert');
  div.textContent = text;
  chatEl.appendChild(div);
  chatEl.scrollTop = chatEl.scrollHeight;
}
